import React from 'react';

type ComboboxA11yStatusProps = {
  isOpen: boolean;
  resultCount: number;
  /** Suppresses the result count while results are still loading. */
  isLoading?: boolean;
  /** Text of the selected item, announced when it changes. */
  selectedItemText?: string;
};

/**
 * Screen-reader announcements for a downshift combobox.
 *
 * Downshift 9 dropped its default `getA11yStatusMessage`, and the prop it
 * kept is recomputed on highlight, open and input changes but not when the
 * items change, so it announces a stale count for lists that load results
 * asynchronously. These live regions are driven by props instead. A live
 * region speaks only when its text changes, so moving the highlight stays
 * silent and the count is read when the number of results changes.
 */
export function ComboboxA11yStatus({
  isOpen,
  resultCount,
  isLoading = false,
  selectedItemText = '',
}: ComboboxA11yStatusProps) {
  return (
    <>
      <div role="status" className="sr-only">
        {resultsMessage(isOpen, resultCount, isLoading)}
      </div>
      <div role="status" className="sr-only">
        {selectedItemText ? `${selectedItemText} has been selected.` : ''}
      </div>
    </>
  );
}

function resultsMessage(
  isOpen: boolean,
  resultCount: number,
  isLoading: boolean
) {
  if (!isOpen || isLoading) {
    return '';
  }
  if (resultCount === 0) {
    return 'No results are available.';
  }
  const results = resultCount === 1 ? 'result is' : 'results are';
  return `${resultCount} ${results} available, use up and down arrow keys to navigate. Press Enter key to select.`;
}
